
import { IconButton } from '@chakra-ui/button';
import { AddIcon, MinusIcon, QuestionIcon } from '@chakra-ui/icons';
import { Center } from '@chakra-ui/layout';
import { Button, FormControl, FormErrorMessage, FormLabel, GridItem, Heading, Icon, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, SimpleGrid, Switch, Tooltip, useToast } from '@chakra-ui/react';
import { Field, Formik, getIn } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { Controller, ControllerConfig } from '../../types';
import { useUpdateControllerMutation } from '../api/api';

import * as Yup from 'yup';

const defaultData: {
	temp: number,
	x: number,
}[] = [
		{
			temp: 15,
			x: 17,
		},
		{
			temp: 15,
			x: 10,
		},
		{
			temp: 15,
			x: 5,
		},
		{
			temp: 15,
			x: -0,
		},
		{
			temp: 15,
			x: -5,
		},
		{
			temp: 15,
			x: -15,
		},
		{
			temp: 15,
			x: -25,
		},
	];


const CustomizedLabel = (changeTemp: any, printOffset: number) => (props: any) => {
	const { x, y, stroke, value, width, index } = props;
	return (
		<foreignObject height={200} width={80} x={x + width / 4} y={y - 25} fill={stroke} fontSize={20} textAnchor="middle">
			<div style={{ width: width / 2 + 'px', height: '35px' }}>
				{printOffset !== 0 && <Center>{printOffset > 0 ? '+' + printOffset : printOffset}</Center>}
			</div>
			<div style={{ width: width / 2 + 'px' }}>
				<IconButton colorScheme={'teal'} aria-label="Search database" onClick={() => {
					changeTemp(index, 1)
				}}>
					<AddIcon />
				</IconButton>
				<Center>{value}</Center>
				<IconButton colorScheme={'teal'} aria-label="Search database" onClick={() => {
					changeTemp(index, -1)
				}}>
					<MinusIcon />
				</IconButton>
			</div>
		</foreignObject>
	);
}


const graphDataFromController = (controller: Controller): { temp: number, x: number, }[] => {

	const thermiaDefaultColumns = [17, 10, 5, 0, -5, -15, -25];

	if (controller.config.heatCurve === null || controller.config.heatCurve.length == 0) {
		return thermiaDefaultColumns.map((temp) => { return { x: temp, temp: 15 } })
	}

	const adjust = controller.config.heatCurveAdjust;
	return controller.config.heatCurve.map((temp, i) => {
		return {
			temp: temp + adjust,
			x: thermiaDefaultColumns[i]
		}
	})
}
const HeatCurveSchema = Yup.object().shape({
	heatCurveAdjust: Yup.number()
		.required('Kan inte vara tomt'),
});

export const HeatCurveSettings = ({ controller }: { controller: Controller | undefined }) => {

	const [graphData, setGraphData] = useState(defaultData);
	const [updateController, updateResult] = useUpdateControllerMutation();
	const toast = useToast()


	useEffect(() => {
		if (!controller) { return; }
		setGraphData(graphDataFromController(controller))
	}, [controller?.config.heatCurve, controller?.config.heatCurveAdjust]);


	if (!controller) {
		return null
	}


	const changeTemp = (index: number, add: number) => {
		setGraphData(graphData.map((row: any, i: number) => {
			if (i === index) {
				return { ...row, temp: row.temp + add }
			}
			return row;
		}))
	}

	return (
		<div>
			<Formik
				initialValues={controller.config}
				validationSchema={HeatCurveSchema}
				onSubmit={(values) => {

					const adjust = controller.config.heatCurveAdjust
					const newValues = {
						...controller, config: {
							...values, heatCurve: graphData.map((row) => {
								return row.temp - adjust;
							})
						}
					}
					updateController(newValues).unwrap().then((controller) => {
						toast({
							title: `${controller.name} värmekurva sparad.`,
							status: "success",
							isClosable: true,
						})
					});
				}}
			>
				{({ handleSubmit, errors, touched, setFieldValue, values }) => (
					<form autoComplete="off" onSubmit={handleSubmit}>

						<SimpleGrid columns={5}   >
							<GridItem colSpan={1}>
								<Heading as='h3' size='md' mb="20px">Generellt</Heading>
								<FormControl >
									<FormLabel >Tillåt styrning av värmekurvan</FormLabel>
									<Switch isChecked={values.heatCurveControlEnabled} colorScheme='teal' size='lg' onChange={(e: ChangeEvent<HTMLInputElement>) => {
										console.log("asdf: ", e.target.value);
										if (e.target.checked) {
											console.log("enable heatacurve");
											setFieldValue("heatCurveControlEnabled", true);
											return;
										}
										setFieldValue("heatCurveControlEnabled", false);
									}} />
								</FormControl>

								{values.heatCurveControlEnabled &&
									<>
										<FormControl isInvalid={!!getIn(errors, 'heatCurveAdjust') && getIn(touched, 'heatCurveAdjust')}>
											<FormLabel >Kurvförskjutning önskad temp <Tooltip label='Motsvarar att öka/minska på "Komfort hjulet" på värmepumpen. 20 grader betyder 0 kurvförskjutning.' fontSize='md'><Icon as={QuestionIcon} color="green.400" /></Tooltip></FormLabel>
											<NumberInput
												size='lg' maxW={32}
												value={(typeof values.heatCurveAdjust == "string") ? '' : values.heatCurveAdjust + 20}
												onChange={(_: string, valueAsNumber: number) => {
													setFieldValue("heatCurveAdjust", Number.isNaN(valueAsNumber) ? '' : valueAsNumber - 20)
												}}
											>
												<NumberInputField />
												<NumberInputStepper>
													<NumberIncrementStepper />
													<NumberDecrementStepper />
												</NumberInputStepper>
											</NumberInput>
											<FormErrorMessage>{getIn(errors, 'heatCurveAdjust')}</FormErrorMessage>
										</FormControl>
										<FormControl isInvalid={!!getIn(errors, 'heatingSeasonStopTemperature') && getIn(touched, 'heatingSeasonStopTemperature')}>
											<FormLabel >Värme säsong stop temperatur</FormLabel>
											<NumberInput
												size='lg' maxW={32}
												value={ values.heatingSeasonStopTemperature }
												onChange={(_: string, valueAsNumber: number) => {
													setFieldValue("heatingSeasonStopTemperature", Number.isNaN(valueAsNumber) ? '' : valueAsNumber )
												}}
											>
												<NumberInputField />
												<NumberInputStepper>
													<NumberIncrementStepper />
													<NumberDecrementStepper />
												</NumberInputStepper>
											</NumberInput>
											<FormErrorMessage>{getIn(errors, 'heatingSeasonStopTemperature')}</FormErrorMessage>
										</FormControl>
									</>
								}
								<Button mt={6} type="submit" colorScheme="teal" width="200px">Spara</Button>
							</GridItem>
							<GridItem colSpan={1}>
								{values.heatCurveControlEnabled &&
									<div style={{ height: "500px", width: '800px', paddingTop: '25px' }}>
										<ResponsiveContainer width="100%" height="100%">
											<BarChart margin={{ left: -30 }} data={graphData} >
												<Bar
													radius={[10, 10, 0, 0]}
													isAnimationActive={false}
													barSize={80}
													fontSize={'100'}
													dataKey="temp"
													fill="var(--chakra-colors-teal-400)"
													label={CustomizedLabel(changeTemp, controller.config.heatCurveAdjust)} />
												<XAxis dataKey="x" />
												<YAxis domain={[0, 60]} />
											</BarChart>
										</ResponsiveContainer>
									</div>
								}
							</GridItem>
						</SimpleGrid>


					</form>
				)}
			</Formik>
		</div >
	);
}
